import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnInit,
  Output,
  Renderer2,
} from '@angular/core';
import {
  MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent,
  MatLegacyAutocompleteTrigger as MatAutocompleteTrigger,
} from '@angular/material/legacy-autocomplete';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { Subject, debounceTime, distinctUntilChanged, noop, tap } from 'rxjs';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { QueryConfig } from 'app/core/models';
import { RelatedToType } from 'app/shared/enums';
import { NewFilterField } from '../../../../models/new-filter-field';
import { FormControl } from '@angular/forms';

type AutocompleteEvent = { value: string; filter: any };

@Component({
  selector: 'app-list-filter-items',
  templateUrl: './list-filter-items.component.html',
  styleUrls: ['./list-filter-items.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListFilterItemsComponent extends OnDestroyMixin implements OnInit {
  @Input() filterRows: any[];
  @Input() newFilterFields: any[];
  @Input() recordResults: any[];
  @Input() typeaheadResults: any[];
  @Input() listStyle: string;
  @Input() queryFilters: Partial<QueryConfig.QueryFilters>;
  @Input() autocompleteSearchOpts;
  @Input() objectType: RelatedToType;
  @Input() allowRelatedObjectFields = false;
  @Input() readonly = false;

  @Output() filterFieldSelected = new EventEmitter<any>();
  @Output() filterValueChange = new EventEmitter<any>();
  @Output() onRecordSearchChange = new EventEmitter<any>();
  @Output() autocompleteRecord = new EventEmitter<any>();
  @Output() autocompleteTypeahead = new EventEmitter<any>();
  @Output() removeFilter = new EventEmitter<any>();
  @Output() filterUpdated = new EventEmitter<any>();
  @Output() autocompleteItemChanged = new EventEmitter<{ key: number; item: any; checked: boolean }>();
  @Output() autocompleteSearchChanged = new EventEmitter<AutocompleteEvent>();
  @Output() onTypeaheadSearchChange = new EventEmitter<AutocompleteEvent>();
  @Output() filterRowsValidityChange = new EventEmitter<boolean>();

  filterText: string = '';
  searchField = new FormControl('');

  private currentAutocompleteOpened: string;
  private typeaheadDebouncer = new Subject<AutocompleteEvent>();

  constructor(private renderer: Renderer2) {
    super();
  }

  ngOnInit(): void {
    this.emitFilterRowsValidity();

    this.typeaheadDebouncer
      .asObservable()
      .pipe(
        untilComponentDestroyed(this),
        debounceTime(500),
        distinctUntilChanged(),
        tap((autocompleteEvent) => this.onTypeaheadSearchChange.emit(autocompleteEvent)),
      )
      .subscribe(noop);
  }

  @HostBinding('style') get customStyle(): string {
    return this.listStyle;
  }

  get allowKeyDates(): boolean {
    return Object.keys(this.queryFilters?.filterOptions || {}).includes('include_keydates');
  }

  /**
   * Check if a filterRow value field is:
   * - a string and has no whitespace and is not empty.
   * - an array and has no empty values.
   * @param filter - The filterRow to check.
   * @returns True if the filteRow is valid.
   */
  public isFilterRowValid(filter: any): boolean {
    const hasEmptyValue = (filter.field as NewFilterField).values?.includes('__empty__') || false;
    const isValueEmpty =
      filter.value === undefined ||
      filter.value?.length === 0 ||
      (typeof filter.value === 'string' && filter.value.trim() === '') ||
      filter.value === null;
    return ((filter.field as NewFilterField) && filter.filter && !isValueEmpty) || hasEmptyValue;
  }

  /**
   * Check if all filterRows are valid.
   * @returns True if all filterRows are valid.
   */
  public isFilterRowsValid(): boolean {
    return this.filterRows.every((filter) => this.isFilterRowValid(filter));
  }

  private emitFilterRowsValidity(): void {
    this.filterRowsValidityChange.emit(this.isFilterRowsValid());
  }

  relayFilterFieldSelected(event: MatSelectChange, filter: any): void {
    this.filterFieldSelected.emit({ event, filter });
    this.emitFilterRowsValidity();
  }

  relayFilterValueChange(value: InputEvent | MatCheckboxChange, filter: any, item: any = {}, blur = false): void {
    let options = {};

    if (blur && (filter.id === 'id' || filter.id.endsWith('.id'))) {
      const valueAsArray = JSON.parse(JSON.stringify((value as unknown as string).split(','))).filter((x) => x);
      this.filterValueChange.emit({ event: valueAsArray, filter, options });
      return;
    }

    if (value instanceof MatCheckboxChange) {
      if (filter.id === 'include_keydates') {
        options = {
          include_keydates: value.checked,
        };
      }

      value = {
        value: { [filter.id.replace('_id', '')]: [item.id, item.name], checked: value.checked },
      } as unknown as InputEvent;
    }

    this.filterValueChange.emit({ event: value, filter, options });
    this.emitFilterRowsValidity();
  }

  relayOnRecordSearchChange(trigger: MatAutocompleteTrigger, value: string, filter: string): void {
    this.onRecordSearchChange.emit({ trigger, value, filter });
  }

  relayAutocompleteRecord(
    event: MatAutocompleteSelectedEvent,
    filter: any,
    type: 'record' | 'typeahead' = 'record',
  ): void {
    if (type === 'record') {
      this.autocompleteRecord.emit({ event, filter });
    } else {
      this.autocompleteTypeahead.emit({ event, filter });
    }
  }

  relayRemoveFilter(index: number): void {
    this.removeFilter.emit(index);
    this.emitFilterRowsValidity();
  }

  operatorChanged(): void {
    this.filterUpdated.emit();
  }

  getRecordName(record: any[] | any): string {
    return Array.isArray(record) && record.length ? record[1] : null;
  }

  onAutocompleteSearch(value, filter): void {
    this.autocompleteSearchChanged.emit({ value, filter });
  }

  selectedAutocompleteItem(filter, item, event): void {
    this.autocompleteItemChanged.emit({ key: filter.id, item, checked: event.checked });
  }

  isAutocompleteItemChecked(filter, item): boolean {
    if (filter.value instanceof Array) {
      const found = filter.value?.find((i) => +i[0] === +item.id);
      return !!found;
    }
  }

  getAutocompleteLabel(filter): string {
    switch (filter.value?.length) {
      case 0:
        return 'Select a value';
      case 1:
        return filter.value[0][1] && filter.value[0][1].length > 15
          ? `${filter.value[0][1].substring(0, 15)}...`
          : filter.value[0][1] || filter.value[0];
      default:
        return filter.value[0][1] && filter.value[0][1].length > 9
          ? `${filter.value[0][1].substring(0, 9)}... +${filter.value?.length - 1}`
          : `${filter.value[0][1] || filter.value[0]} +${filter.value?.length - 1}`;
    }
  }

  @HostListener('document:click', ['$event'])
  autocompleteClickOut(event) {
    const opened = document.querySelector(`${this.currentAutocompleteOpened}`);
    if (opened && !opened.contains(event.target)) {
      this.closeAutocomplete();
    }
  }

  showAutocompleteOptions(index: number, filter): void {
    if (this.currentAutocompleteOpened) {
      this.closeAutocomplete();
    }

    const classElement = `.dropdown-autocomplete.autocomplete-${index}`;
    const idElement = `#autocomplete-field-${index}`;
    const element = document.querySelector(classElement);
    this.onAutocompleteSearch('', filter);
    if (element && !element.classList.contains('show')) {
      this.renderer.addClass(element, 'autocomplete-show');
      setTimeout(() => {
        element.scroll({ top: 0, left: 0, behavior: 'smooth' });
        element.querySelector('input').focus();
      }, 100); // not yet in the screen
      this.currentAutocompleteOpened = idElement;
    }
  }

  getRecordType(filter) {
    if (filter?.field?.record_type)
      return filter?.field?.record_type?.startsWith('co') ? 'CustomObject' : filter?.field?.record_type;
    return filter.id;
  }

  private closeAutocomplete(): void {
    const elementToClose = document.querySelector(`${this.currentAutocompleteOpened} .autocomplete-show`);
    if (elementToClose) {
      this.renderer?.removeClass(elementToClose, 'autocomplete-show');
      this.currentAutocompleteOpened = undefined;
    }
  }

  onTypeaheadChange(value: string, filter: any): void {
    this.typeaheadDebouncer.next({ value, filter });
  }

  fieldSelectionOpenedChange(): void {
    this.searchField.setValue('');
  }

  hasSearchMatchInGroup(searchCriteria: string, items: any[]): boolean {
    if (!searchCriteria) {
      return true;
    }

    return !!items.find(
      (x) => !x.exclude_in_nested_filter && x.name?.toLowerCase().indexOf(searchCriteria.toLowerCase()) > -1,
    );
  }
}
